import { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import Button from '../../ui/Button';
import nurseryService from '../../../services/nursery';
import { errorMessage } from '../../../utils/apiResponse';
import { getTodayInTz, useOrgTimezone } from '../../../utils/timezone';
import type { NurseryLocation, NurseryLot, NurseryTransfer } from '../../../types/nursery';

/**
 * Move trees from one farm to another by hand: pick the farms, where they go,
 * and which lots — all of a lot moves it, part of one splits it on arrival.
 */

export interface NewTransferDialogProps {
  onClose: () => void;
  onCreated: (t: NurseryTransfer) => void;
}

const inputCls = 'w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm';

export function NewTransferDialog({ onClose, onCreated }: NewTransferDialogProps) {
  const tz = useOrgTimezone();
  const [farms, setFarms] = useState<{ id: number; name: string }[]>([]);
  const [fromFarm, setFromFarm] = useState<number | ''>('');
  const [toFarm, setToFarm] = useState<number | ''>('');
  const [toLocations, setToLocations] = useState<NurseryLocation[]>([]);
  const [toLocation, setToLocation] = useState<number | ''>('');
  const [date, setDate] = useState(getTodayInTz(tz));
  const [vehicle, setVehicle] = useState('');
  const [lots, setLots] = useState<NurseryLot[] | null>(null);
  const [qty, setQty] = useState<Record<number, number | ''>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    nurseryService.getPickers().then((res) => res.success && setFarms(res.data.farms)).catch(() => undefined);
  }, []);

  // Lots at the source farm: the lots whose location is one of its locations.
  useEffect(() => {
    if (!fromFarm) { setLots(null); return; }
    setLots(null);
    setQty({});
    Promise.all([nurseryService.getLocations({ farm_id: fromFarm }), nurseryService.getLots({ status: 'active' })])
      .then(([locs, all]) => {
        const here = new Set((locs.success ? locs.data : []).map((l) => l.id));
        setLots((all.success ? all.data : []).filter((l) => l.location_id != null && here.has(l.location_id) && l.current_count > 0));
      })
      .catch((e) => { setLots([]); setError(errorMessage(e, 'Could not load lots')); });
  }, [fromFarm]);

  useEffect(() => {
    if (!toFarm) { setToLocations([]); setToLocation(''); return; }
    nurseryService.getLocations({ farm_id: toFarm, active: true }).then((res) => {
      const list = (res.success ? res.data : []).slice()
        .sort((a, b) => Number(!!b.is_despatch_area) - Number(!!a.is_despatch_area) || a.name.localeCompare(b.name));
      setToLocations(list);
      setToLocation(list[0]?.id ?? '');
    }).catch(() => setToLocations([]));
  }, [toFarm]);

  const lines = useMemo(
    () => Object.entries(qty).filter(([, q]) => q !== '' && Number(q) > 0).map(([id, q]) => ({ lot_id: Number(id), quantity: Number(q) })),
    [qty],
  );
  const trees = lines.reduce((n, l) => n + l.quantity, 0);

  const create = async () => {
    if (!fromFarm || !toFarm || !toLocation || !lines.length) {
      setError('Choose both farms, where the trees go, and at least one lot.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await nurseryService.createTransfer({
        from_farm_id: fromFarm, to_farm_id: toFarm, to_location_id: toLocation, scheduled_date: date,
        vehicle: vehicle || null, lines,
      });
      if (res.success) onCreated(res.data);
      else setError(errorMessage(res, 'Could not create the transfer'));
    } catch (e) {
      setError(errorMessage(e, 'Could not create the transfer'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4" role="dialog" aria-modal="true" aria-labelledby="new-transfer-title">
      <div className="bg-white w-full sm:max-w-xl rounded-t-2xl sm:rounded-xl shadow-xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-start justify-between gap-3 px-5 pt-5">
          <h2 id="new-transfer-title" className="text-lg font-semibold text-neutral-900">New transfer</h2>
          <button type="button" onClick={onClose} aria-label="Close" className="p-1 rounded hover:bg-neutral-100 flex-shrink-0">
            <X className="h-5 w-5 text-neutral-500" />
          </button>
        </div>
        <div className="px-5 py-4 space-y-3">
          {error && <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700">{error}</div>}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label htmlFor="nt-from" className="block text-sm font-medium text-neutral-700 mb-1">From farm</label>
              <select id="nt-from" value={fromFarm} onChange={(e) => setFromFarm(e.target.value ? Number(e.target.value) : '')} className={inputCls}>
                <option value="">Choose…</option>
                {farms.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="nt-to" className="block text-sm font-medium text-neutral-700 mb-1">To farm</label>
              <select id="nt-to" value={toFarm} onChange={(e) => setToFarm(e.target.value ? Number(e.target.value) : '')} className={inputCls}>
                <option value="">Choose…</option>
                {farms.filter((f) => f.id !== fromFarm).map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="nt-loc" className="block text-sm font-medium text-neutral-700 mb-1">Put them in</label>
              <select id="nt-loc" value={toLocation} onChange={(e) => setToLocation(e.target.value ? Number(e.target.value) : '')} className={inputCls} disabled={!toFarm}>
                {!toLocations.length && <option value="">{toFarm ? 'No locations at that farm' : 'Choose a farm first'}</option>}
                {toLocations.map((l) => <option key={l.id} value={l.id}>{l.name}{l.is_despatch_area ? ' (despatch area)' : ''}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="nt-date" className="block text-sm font-medium text-neutral-700 mb-1">Move on</label>
              <input id="nt-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inputCls} />
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="nt-vehicle" className="block text-sm font-medium text-neutral-700 mb-1">Vehicle (optional)</label>
              <input id="nt-vehicle" value={vehicle} onChange={(e) => setVehicle(e.target.value)} className={inputCls} />
            </div>
          </div>

          {fromFarm !== '' && (
            <div>
              <p className="text-sm font-medium text-neutral-700 mb-1">Lots to send</p>
              {lots === null ? (
                <div className="h-16 bg-neutral-100 rounded-lg animate-pulse" />
              ) : lots.length === 0 ? (
                <p className="text-sm text-neutral-500">No active lots at that farm.</p>
              ) : (
                <ul className="divide-y divide-neutral-100 border border-neutral-200 rounded-lg max-h-72 overflow-y-auto">
                  {lots.map((l) => (
                    <li key={l.id} className="flex items-center gap-3 px-3 py-2">
                      <div className="min-w-0 flex-1 text-sm">
                        <p className="font-medium text-neutral-900 break-words">
                          {l.cultivar_name}{l.rootstock_name ? ` / ${l.rootstock_name}` : ''}
                        </p>
                        <p className="text-xs text-neutral-500 break-words">
                          {[l.tree_type_name, l.lot_code ? `Lot ${l.lot_code}` : null, l.location_name].filter(Boolean).join(' · ')}
                          {' · '}{l.current_count} there{l.current_stage === 'ready' ? ', ready' : ''}
                        </p>
                      </div>
                      <input type="number" inputMode="numeric" min={0} max={l.current_count} aria-label={`How many from ${l.lot_code ?? l.cultivar_name}`}
                        value={qty[l.id] ?? ''} placeholder="0"
                        onChange={(e) => setQty((q) => ({ ...q, [l.id]: e.target.value === '' ? '' : Math.min(l.current_count, Math.max(0, Number(e.target.value))) }))}
                        className="w-20 border border-neutral-300 rounded-lg px-2 py-1.5 text-sm text-right" />
                      <button type="button" className="text-xs text-primary-700 underline whitespace-nowrap"
                        onClick={() => setQty((q) => ({ ...q, [l.id]: l.current_count }))}>All</button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2 px-5 pb-5">
          {trees > 0 && <span className="text-sm text-neutral-600 mr-auto">{lines.length} lot{lines.length === 1 ? '' : 's'}, {trees.toLocaleString()} trees</span>}
          <Button variant="outline" size="sm" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button size="sm" onClick={create} disabled={busy}>Create transfer</Button>
        </div>
      </div>
    </div>
  );
}

export default NewTransferDialog;
